import React, {useEffect, useMemo, useState} from 'react';
import Box from "@mui/material/Box";
import {
    Typography,
    Avatar,
    Grid,
    FormLabel,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Tooltip,
    Button,
} from "@mui/material";
import {useDropzone} from "react-dropzone";
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {storeAppointmentAttachment} from "../../../../../store/crm/appointment/attachment";
import {useDispatch} from "react-redux";
import {getAppointmentDetails} from "../../../../../store/crm/appointment";
import Waveloading from '../../../../loading/Waveloading';
import toast from 'react-hot-toast';
import { NOTE_MAX_LENGTH } from '../../../../../utils/noteLimits';
import NoteLimitCounter from "../../../../../components/note-limit/NoteLimitCounter";

const BURGUNDY = '#A72C68';
const BURGUNDY_DARK = '#921F58';
const PINK_BG = '#FAEFF5';
const PINK_BORDER = '#F0CDDB';

const labelStyles = {
    color: BURGUNDY,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    mb: '6px',
    "&.Mui-focused": {
        color: `${BURGUNDY}!important`,
    }
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const ACCEPT_FILES = {
    'application/pdf': ['.pdf'],
    'application/msword': [],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
    'text/csv': ['.csv'],
    'application/vnd.ms-excel': ['.csv'],
};

const AddAttachment = ({appointmentDetails}) => {
    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedServices, setSelectedServices] = useState({})
    const [comments, setComments] = useState('')

    const dispatch = useDispatch()

    const handleSubmit = async () => {
        if (files?.length && selectedServices?.id) {

            setLoading(true)
            const formData = new FormData();

            formData.append("booking_id", appointmentDetails?.id);
            formData.append("service_id", selectedServices?.id);
            formData.append("comment", comments);

            files.forEach(value => formData.append("files" + '[]', value))
            let res = await dispatch(storeAppointmentAttachment({data: formData, booking_id: appointmentDetails?.id}))

            setLoading(false)
            if (res?.payload?.success === 'true') {
                setSelectedServices({})
                dispatch(getAppointmentDetails(appointmentDetails?.id))
                setFiles([])
                setComments('')
                toast.success(`Attachment added successfully!`)
            }
        } else {
            toast.error("All Fields are required", ``, 'error')
        }
    }

    const handleRemove = (file) => {
        if (files?.length) {
            let finalData = files.filter((item) => file?.path !== item?.path && file?.size !== item?.size)
            setFiles(finalData)
        }
    }

    const {acceptedFiles, isDragActive, isDragAccept, isDragReject, getRootProps, getInputProps} = useDropzone({
        accept: ACCEPT_FILES,
    });

    useEffect(() => {
        if (acceptedFiles?.length) {
            const merged = [...files, ...acceptedFiles];
            const unique = merged.filter((f, i, self) =>
                i === self.findIndex(x => x.name === f.name && x.size === f.size)
            );
            setFiles(unique);
        }
    }, [acceptedFiles]);

    const dropzoneStyle = useMemo(() => ({
        borderColor: isDragReject ? '#EF4444' : isDragAccept ? '#10B981' : isDragActive ? BURGUNDY : PINK_BORDER,
        background: isDragActive ? '#F5DCE6' : PINK_BG,
    }), [isDragActive, isDragAccept, isDragReject]);

    return (
        <Box
            sx={{
                background: '#fff',
                borderRadius: '14px',
                border: `1px solid ${PINK_BORDER}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                p: {sm: '20px', xs: '14px'},
                position: 'relative',
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25}}>
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        background: PINK_BG,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <AttachFileOutlinedIcon sx={{color: BURGUNDY, fontSize: 20}}/>
                </Box>
                <Box>
                    <Typography sx={{fontSize: {xs: '15px', sm: '17px'}, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold'}}>
                        Add Attachment
                    </Typography>
                    <Typography sx={{fontSize: '11px', color: '#6B7280', mt: '2px'}}>
                        Upload a document for this appointment.
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    background: '#FFF5F5',
                    border: '1px solid #FECACA',
                    color: '#B91C1C',
                    px: 1.5,
                    py: 1,
                    borderRadius: '10px',
                    fontSize: '12px',
                    mb: 2,
                }}
            >
                Note: Only upload doc files here. To upload Images/Videos, please use the "Photo Gallery" option.
            </Box>

            {loading ? <Waveloading cover/> : ''}

            {/* Dropzone */}
            <Box
                {...getRootProps()}
                sx={{
                    border: `2px dashed ${dropzoneStyle.borderColor}`,
                    background: dropzoneStyle.background,
                    borderRadius: '14px',
                    p: {xs: 2.5, sm: 3},
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    maxWidth: 360,
                    '&:hover': {borderColor: BURGUNDY, background: '#F5DCE6'},
                }}
            >
                <input {...getInputProps()}/>
                <Box sx={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: `linear-gradient(135deg, ${BURGUNDY} 0%, ${BURGUNDY_DARK} 100%)`,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 8px 22px rgba(167,44,104,0.30)',
                    mb: 1,
                }}>
                    <CloudUploadOutlinedIcon sx={{color: '#fff', fontSize: 24}}/>
                </Box>
                <Typography sx={{fontSize: '14px', fontWeight: 800, color: '#1a1a2e', fontFamily: 'helvetica-lt-std-bold'}}>
                    Drop files to upload
                </Typography>
                <Typography sx={{fontSize: '11px', color: '#6B7280', mt: 0.5}}>
                    PDF, Word, CSV — or click to browse
                </Typography>
            </Box>

            {/* Selected file previews */}
            {files?.length > 0 && (
                <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2}}>
                    {files.map((file, key) => (
                        <Box key={key} sx={{position: 'relative'}}>
                            <Tooltip title={file?.path || file?.name}>
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 110,
                                        height: 110,
                                        borderRadius: '10px',
                                        border: `1px solid ${PINK_BORDER}`,
                                        background: PINK_BG,
                                        color: BURGUNDY,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.4px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {(file?.name || '').split('.').pop() || 'DOC'}
                                </Avatar>
                            </Tooltip>
                            <Typography
                                sx={{
                                    mt: 0.75,
                                    fontSize: '11px',
                                    color: '#374151',
                                    maxWidth: 110,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {file?.name}
                            </Typography>
                            <IconButton
                                onClick={() => handleRemove(file)}
                                sx={{
                                    position: 'absolute',
                                    top: -8, right: -8,
                                    width: 26, height: 26,
                                    background: '#EB5757',
                                    border: '2px solid #fff',
                                    '&:hover': {background: '#D44747'},
                                }}
                            >
                                <DeleteOutlineIcon sx={{color: '#fff', fontSize: 14}}/>
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}

            <Grid container spacing={2.5} sx={{mt: 1}}>
                <Grid item xs={12} xl={6}>
                    <FormControl fullWidth>
                        <FormLabel sx={{...labelStyles}}>Service Name</FormLabel>
                        <Select
                            size={'small'}
                            value={selectedServices?.id || ''}
                            displayEmpty
                            MenuProps={MenuProps}
                            renderValue={() => selectedServices?.name || <Box component="span" sx={{color: '#9CA3AF'}}>Select a service</Box>}
                            sx={{
                                background: PINK_BG,
                                borderRadius: '10px',
                                '& .MuiOutlinedInput-notchedOutline': {borderColor: PINK_BORDER},
                                '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: BURGUNDY},
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: BURGUNDY},
                            }}
                        >
                            {appointmentDetails?.services?.length > 0 && appointmentDetails?.services.map((service, key) => (
                                <MenuItem
                                    key={key}
                                    value={service?.id}
                                    onClick={() => setSelectedServices(service)}
                                    sx={{whiteSpace: 'normal'}}
                                >
                                    {service?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} xl={6}>
                    <FormControl fullWidth>
                        <FormLabel sx={{...labelStyles}}>Comments</FormLabel>
                        <Box sx={{
                            position: 'relative',
                            border: `1px solid ${PINK_BORDER}`,
                            borderRadius: '10px',
                            background: PINK_BG,
                            transition: 'border-color 0.15s',
                            '&:focus-within': {borderColor: BURGUNDY, boxShadow: '0 0 0 2px rgba(167,44,104,0.10)'},
                        }}>
                            <textarea
                                rows={3}
                                placeholder="Add a brief note about this attachment…"
                                maxLength={NOTE_MAX_LENGTH}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                style={{
                                    width: '100%',
                                    resize: 'vertical',
                                    padding: '10px 12px 22px',
                                    border: 'none',
                                    outline: 'none',
                                    borderRadius: '10px',
                                    fontFamily: 'inherit',
                                    fontSize: '14px',
                                    color: '#1a1a2e',
                                    background: 'transparent',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <NoteLimitCounter value={comments} />
                        </Box>
                    </FormControl>
                </Grid>
                <Grid xs={12} item>
                    <Button
                        onClick={handleSubmit}
                        startIcon={<SaveOutlinedIcon sx={{fontSize: '16px !important'}}/>}
                        sx={{
                            height: 44, px: 3,
                            borderRadius: '10px',
                            textTransform: 'capitalize',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#fff',
                            background: "linear-gradient(135deg, #27AE60 0%, #1E8449 100%)",
                            border: "1px solid #27AE60",
                            boxShadow: "none",
                            '&:hover': {
                                background: "#219653",
                                color: "#fff",
                                border: "1px solid #27AE60",
                                boxShadow: "none",
                            },
                        }}
                    >
                        Save Attachment
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AddAttachment;
